import {
  LayoutDashboard,
  ClipboardList,
  Users,
  BarChart3,
  LogOut,
  User,
  X,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";

function Sidebar({ show, closeSidebar }) {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ reactive user
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")));
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem("sidebar_collapsed") === "true";
  });

  useEffect(() => {
    const updateUser = () => {
      setUser(JSON.parse(localStorage.getItem("user")));
    };
    window.addEventListener("storageUpdate", updateUser);
    return () => window.removeEventListener("storageUpdate", updateUser);
  }, []);

  useEffect(() => {
    const handleGlobalToggle = () => {
      setIsCollapsed(prev => !prev);
    };
    window.addEventListener("toggleSidebarCollapse", handleGlobalToggle);
    return () => window.removeEventListener("toggleSidebarCollapse", handleGlobalToggle);
  }, []);

  useEffect(() => {
    document.body.setAttribute("data-sidebar-collapsed", isCollapsed ? "true" : "false");
    localStorage.setItem("sidebar_collapsed", isCollapsed ? "true" : "false");
    window.dispatchEvent(new Event("sidebarCollapseChanged"));
  }, [isCollapsed]);

  const toggleCollapse = (e) => {
    e.stopPropagation();
    setIsCollapsed(prev => !prev);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const isActive = (path) => location.pathname.startsWith(path);
  const role = user?.role?.toLowerCase()?.replace("_", " ");

  return (
    <aside className={`sidebar ${show ? "open" : ""} ${isCollapsed ? "collapsed" : ""}`}>
      <div className="sb-inner">
        {/* LOGO & EXPAND / COLLAPSE HEADER */}
        <div 
          className="sb-logo-row" 
          onClick={isCollapsed ? toggleCollapse : undefined}
          style={{ cursor: isCollapsed ? 'pointer' : 'default' }}
          title={isCollapsed ? "Click to expand sidebar" : ""}
        >
          <div className="sb-logo">
            <div className="li">FL</div>
            <div className="sb-logo-text">
              <div className="ln">Four Leaf IT</div>
              <div className="ls">Job Tracking</div>
            </div>
          </div>

          <div className="sb-header-actions">
            <button
              className="sb-collapse-btn"
              onClick={toggleCollapse}
              title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
              aria-label={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
              type="button"
            >
              {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>

            <button 
              className="sb-close-btn" 
              onClick={closeSidebar}
              aria-label="Close sidebar"
              type="button"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Floating Expand Button when Collapsed */}
        {isCollapsed && (
          <button 
            className="sb-collapsed-expand-btn"
            onClick={toggleCollapse}
            title="Expand Sidebar"
            aria-label="Expand Sidebar"
            type="button"
          >
            <ChevronRight size={16} />
          </button>
        )}

        <div className="divider"></div>

        <div className="sb-section">MAIN</div>

        <button
          className={`sb-btn ${isActive("/dashboard") ? "act" : ""}`}
          onClick={() => {
            navigate("/dashboard");
            closeSidebar();
          }}
          title="Dashboard"
        >
          <LayoutDashboard size={18} />
          <span className="sb-btn-text">Dashboard</span>
        </button>

        {(role === "admin" || role === "supervisor" || role === "team leader") && (
          <button
            className={`sb-btn ${isActive("/create-job") ? "act" : ""}`}
            onClick={() => {
              navigate("/create-job");
              closeSidebar();
            }}
            title="Job Forms"
          >
            <ClipboardList size={18} />
            <span className="sb-btn-text">Job Forms</span>
          </button>
        )}

        <div className="sb-section">MANAGEMENT</div>

        {(role === "admin" || role === "supervisor" || role === "team leader") && (
          <button
            className={`sb-btn ${isActive("/users") ? "act" : ""}`}
            onClick={() => {
              navigate("/users");
              closeSidebar();
            }}
            title="User Management"
          >
            <Users size={18} />
            <span className="sb-btn-text">User Management</span>
          </button>
        )}

        <button
          className={`sb-btn ${isActive("/job/reports") ? "act" : ""}`}
          onClick={() => {
            navigate("/job/reports");
            closeSidebar();
          }}
          title="Reports"
        >
          <BarChart3 size={18} />
          <span className="sb-btn-text">Reports</span>
        </button>

        <div className="sb-spacer"></div>

        {/* ✅ PROFILE BUTTON */}
        <button
          className="sb-btn profile-btn"
          onClick={() => {
            window.dispatchEvent(new Event("openProfileModal"));
            closeSidebar();
          }}
          title="Profile Settings"
        >
          <User size={18} />
          <span className="sb-btn-text">Profile Settings</span>
        </button>

        {/* USER INFO */}
        <div className="sb-user" title={`${user?.name || "Guest"} (${user?.role || "User"})`}>
          <div className="sb-ava">
            {user?.name ? user.name.charAt(0).toUpperCase() : "U"}
          </div>
          <div className="sb-user-text">
            <div className="sb-uname">{user?.name || "Guest"}</div>
            <div className="sb-urole">{user?.role || "User"}</div>
          </div>
        </div>

        <button className="sb-btn logout" onClick={handleLogout} title="Logout">
          <LogOut size={18} />
          <span className="sb-btn-text">Logout</span>
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;